import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { clearToken, startProactiveRefresh } from '../utils/tokenManager';
import { clearAllCookies, getRedirectURL } from '../utils/cookieUtils';
import { clearPolicyBotDomainCache } from '../policybotDomainCache';
import { persistDomainsList } from '../utils/domainStorage';
import { USER_DATA_STORAGE_KEYS } from '../utils/constants';

const AuthContext = createContext(null);

/**
 * Extract tokens from the OAuth redirect URL fragment and persist them in
 * sessionStorage. Only the fragment is read: it is never sent to servers or
 * logged, unlike a query string. Required because UI and API are on different
 * domains, so HttpOnly API cookies are treated as third-party and often blocked.
 */
export const extractTokensFromURL = () => {
  if (typeof window === 'undefined') return null;

  const hash = window.location.hash;

  if (hash && (hash.includes('access_token=') || hash.includes('id_token='))) {
    const params = new URLSearchParams(hash.substring(1));
    const appToken = params.get('id_token');

    if (appToken) {
      const graphToken = params.get('graph_token') || params.get('access_token');
      const refreshToken = params.get('refresh_token');
      sessionStorage.setItem('access_token', appToken);
      if (graphToken) sessionStorage.setItem('graph_token', graphToken);
      if (refreshToken) sessionStorage.setItem('refresh_token', refreshToken);
      window.history.replaceState(null, '', window.location.pathname + window.location.search);
      return appToken;
    }
  }

  return sessionStorage.getItem('access_token');
};

let exchangeState = { code: null, promise: null };

/**
 * Trade the single-use `?code=` from the SSO redirect for session tokens.
 * Shared by every caller because the code works once and effects can run twice.
 */
export const exchangeLoginCode = (backendURL) => {
  if (typeof window === 'undefined') return Promise.resolve(false);

  const code = new URLSearchParams(window.location.search).get('code');
  if (!code) return exchangeState.promise || Promise.resolve(false);
  if (exchangeState.code === code && exchangeState.promise) return exchangeState.promise;

  const url = new URL(window.location.href);
  url.searchParams.delete('code');
  window.history.replaceState(null, '', url.pathname + url.search + url.hash);

  const promise = (async () => {
    try {
      const response = await fetch(`${backendURL}/api/auth/exchange/`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ code }),
      });
      if (!response.ok) return false;
      const data = await response.json();
      if (!data.id_token) return false;
      sessionStorage.setItem('access_token', data.id_token);
      if (data.graph_token) sessionStorage.setItem('graph_token', data.graph_token);
      if (data.refresh_token) sessionStorage.setItem('refresh_token', data.refresh_token);
      return true;
    } catch (error) {
      console.error('Login code exchange failed:', error);
      return false;
    }
  })();

  exchangeState = { code, promise };
  return promise;
};

/**
 * AuthProvider - Centralized authentication state management
 *
 * Auth state is sourced from the server (/api/protected/) on every mount.
 * HttpOnly cookies and/or Bearer token headers in sessionStorage are supported.
 */
export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);

  const proactiveRefreshCleanup = useRef(null);

  const backendURL = process.env.REACT_APP_TWCHATBOT_DOMAINS;
  const frontendURL = process.env.REACT_APP_FRONTEND_URL;

  /**
   * Handle user login
   * Only sets React state — no localStorage writes.
   * The server has already set the HttpOnly cookies.
   */
  const handleLogin = useCallback(({ fullname, email, role }) => {
    setIsLoggedIn(true);
    setEmail(email);
    setRole(role || '');
    setIsAuthenticated(true);
    setUsername(fullname);
  }, []);

  const handleLogout = useCallback(async () => {
    // Stop proactive refresh
    if (proactiveRefreshCleanup.current) {
      proactiveRefreshCleanup.current();
      proactiveRefreshCleanup.current = null;
    }

    try {
      await fetch(`${backendURL}/api/logout/`, {
        method: "POST",
        credentials: "include",
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
      });
    } catch (err) {
      console.error('Logout API call failed:', err);
    }

    // Clear React state
    setIsAuthenticated(false);
    setIsLoggedIn(false);
    setUsername("");
    setEmail("");
    setRole("");

    // Clear caches
    clearPolicyBotDomainCache();

    // Clear auth state and per-user content (chats, transcripts); leave UI preferences alone.
    USER_DATA_STORAGE_KEYS.forEach((key) => {
      try { localStorage.removeItem(key); } catch (e) { /* storage unavailable */ }
      try { sessionStorage.removeItem(key); } catch (e) { /* storage unavailable */ }
    });

    clearToken();
    clearAllCookies();

    // Redirect
    window.location.replace(frontendURL || getRedirectURL());

  }, [backendURL, frontendURL]);

  /**
   * Fetch protected data from the server.
   * This is the ONLY source of truth for user identity and role.
   */
  const fetchProtectedData = useCallback(async () => {
    try {
      await exchangeLoginCode(backendURL);
      const storedToken = extractTokensFromURL();
      const storedGraphToken = sessionStorage.getItem('graph_token');
      const storedRefreshToken = sessionStorage.getItem('refresh_token');

      const headers = {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      };
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      if (storedGraphToken) {
        headers['X-Graph-Token'] = storedGraphToken;
      }
      if (storedRefreshToken) {
        headers['X-Refresh-Token'] = storedRefreshToken;
      }

      const response = await fetch(`${backendURL}/api/protected/`, {
        method: "GET",
        credentials: "include", // Automatically attach HTTP-only cookies
        headers
      });

      // Handle 401/403 responses
      if (response.status === 401 || response.status === 403) {
        console.error('Authentication failed:', response.status);
        // Don't call handleLogout here if we're in initial load —
        // just mark as not authenticated
        setIsAuthenticated(false);
        setIsLoggedIn(false);
        setUsername("");
        setEmail("");
        setRole("");
        return null;
      }

      if (!response.ok) {
        throw new Error(`API call failed: ${response.status}`);
      }


      const data = await response.json();

      if (data.user) {
        const userData = {
          email: data.user.preferred_name || data.user.email || '',
          role: data.user.role || '',
          username: data.user.name || '',
        };
        setEmail(userData.email);
        setRole(userData.role);
        setUsername(userData.username);
        setIsAuthenticated(true);
        setIsLoggedIn(true);

        // Write display-only metadata for UI presentation (e.g. ChatInterface avatar initials).
        // WARNING: This data is strictly non-authoritative. All auth & permission checks MUST be validated server-side.
        try {
          const userPayload = JSON.stringify({
            fullname: userData.username,
            email: userData.email,
            role: userData.role,
          });
          localStorage.setItem('user', userPayload);
          localStorage.setItem('formData', userPayload); // Legacy support
          if (data.user && data.user.sr_id) {
            localStorage.setItem('sr_id', data.user.sr_id);
          }
          persistDomainsList(data.domains_list);
        } catch (e) {
          console.error("Failed to write display metadata to localStorage", e);
        }
        return userData;
      }
      return null;
    } catch (error) {
      console.error("Error fetching protected data:", error);
      return null;
    }
  }, [backendURL]);

  // Verify auth from server on mount (replaces localStorage read)
  useEffect(() => {
    const verifyOnMount = async () => {
      setLoading(true);
      await fetchProtectedData();
      setLoading(false);
    };
    verifyOnMount();
  }, [fetchProtectedData]);

  // Start proactive refresh when authenticated, clean up when not
  useEffect(() => {
    if (isAuthenticated && backendURL) {
      proactiveRefreshCleanup.current = startProactiveRefresh(
        backendURL,
        handleLogout
      );
    }

    return () => {
      if (proactiveRefreshCleanup.current) {
        proactiveRefreshCleanup.current();
        proactiveRefreshCleanup.current = null;
      }
    };
  }, [isAuthenticated, backendURL, handleLogout]);

  const value = {
    // State
    isAuthenticated,
    username,
    email,
    role,
    loading,
    isLoggedIn,
    backendURL,
    frontendURL,

    // Functions
    handleLogin,
    handleLogout,
    fetchProtectedData,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use auth context
 * Throws error if used outside AuthProvider
 */
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
